/**
 * usePublicInterview Hook
 *
 * Manages state for the public interview flow (no auth).
 * Used by interviewees who access an interview via a share link.
 */

import { useState, useCallback } from 'react'

import type { PublicInterviewLandingData, AIInterviewMessage, AIInterviewProgressData } from '@ispot/shared'

import {
  publicInterviewApi,
  type AIInterviewStreamEvent,
} from '@/lib/api/client'
import { stripMetadataBlock } from '@/lib/utils/stripMetadataBlock'

// ============================================================================
// TYPES
// ============================================================================

export interface PublicInterviewState {
  landing: PublicInterviewLandingData | null
  sessionId: string | null
  sessionToken: string | null
  messages: AIInterviewMessage[]
  mcqOptions: string[]
  progress: AIInterviewProgressData | null
  conversationStartedAt: string | null
  isLoading: boolean
  isStreaming: boolean
  error: string | null
  phase: 'loading' | 'landing' | 'conversation' | 'completed' | 'error'
}

// ============================================================================
// HOOK
// ============================================================================

export function usePublicInterview(shareToken: string) {
  const [state, setState] = useState<PublicInterviewState>({
    landing: null,
    sessionId: null,
    sessionToken: null,
    messages: [],
    mcqOptions: [],
    progress: null,
    conversationStartedAt: null,
    isLoading: false,
    isStreaming: false,
    error: null,
    phase: 'loading',
  })

  const fetchLanding = useCallback(async () => {
    setState(s => ({ ...s, isLoading: true, error: null, phase: 'loading' }))
    try {
      const landing = await publicInterviewApi.getLanding(shareToken)
      setState(s => ({ ...s, landing, isLoading: false, phase: 'landing' }))
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load interview'
      setState(s => ({ ...s, isLoading: false, error: message, phase: 'error' }))
    }
  }, [shareToken])

  const startInterview = useCallback(async (
    name?: string,
    email?: string,
    consentGiven = true,
  ) => {
    setState(s => ({ ...s, isLoading: true, error: null }))
    try {
      const result = await publicInterviewApi.startInterview(
        shareToken,
        { name, email, consentGiven },
      )
      const { sessionId, sessionToken, openingMessage, mcqOptions: openingMcq } = result

      // Seed conversation with the opening AI message
      const initialMessages: AIInterviewMessage[] = openingMessage
        ? [{
            id: crypto.randomUUID(),
            sessionId,
            role: 'ai' as const,
            content: openingMessage,
            inputMethod: null,
            questionIntent: 'introduction',
            extractedEntities: [],
            mcqOptions: openingMcq || null,
            selectedMcqOption: null,
            createdAt: new Date().toISOString(),
          }]
        : []

      setState(s => ({
        ...s,
        sessionId,
        sessionToken,
        messages: initialMessages,
        mcqOptions: openingMcq || [],
        conversationStartedAt: new Date().toISOString(),
        isLoading: false,
        phase: 'conversation',
      }))
      return { sessionId, sessionToken }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to start interview'
      setState(s => ({ ...s, isLoading: false, error: message }))
      return null
    }
  }, [shareToken])

  const sendMessage = useCallback(async (
    content: string,
    inputMethod?: string,
    selectedMcqOption?: string,
  ) => {
    if (!state.sessionId || !state.sessionToken) return

    const sessionId = state.sessionId
    const sessionToken = state.sessionToken

    // Add user message optimistically
    const userMessage: AIInterviewMessage = {
      id: crypto.randomUUID(),
      sessionId,
      role: 'user',
      content,
      inputMethod: inputMethod || 'text',
      questionIntent: null,
      extractedEntities: [],
      mcqOptions: null,
      selectedMcqOption: selectedMcqOption || null,
      createdAt: new Date().toISOString(),
    }

    // Add placeholder for streaming AI response
    const placeholderMessage: AIInterviewMessage = {
      id: 'streaming',
      sessionId,
      role: 'ai',
      content: '',
      inputMethod: null,
      questionIntent: null,
      extractedEntities: [],
      mcqOptions: null,
      selectedMcqOption: null,
      createdAt: new Date().toISOString(),
    }

    setState(s => ({
      ...s,
      messages: [...s.messages, userMessage, placeholderMessage],
      mcqOptions: [],
      isStreaming: true,
      error: null,
    }))

    try {
      await publicInterviewApi.sendMessage(
        shareToken,
        sessionId,
        sessionToken,
        { content, inputMethod, selectedMcqOption },
        (event: AIInterviewStreamEvent) => {
          if (event.type === 'text') {
            const text = (event as { content: string }).content
            setState(s => ({
              ...s,
              messages: s.messages.map(m =>
                m.id === 'streaming'
                  ? { ...m, content: stripMetadataBlock(text) }
                  : m,
              ),
            }))
          } else if (event.type === 'metadata') {
            const options = (event as { mcqOptions?: string[] }).mcqOptions || []
            setState(s => ({ ...s, mcqOptions: options }))
          } else if (event.type === 'progress') {
            const progressData = event as unknown as AIInterviewProgressData
            setState(s => ({ ...s, progress: progressData }))
          } else if (event.type === 'done') {
            setState(s => ({
              ...s,
              isStreaming: false,
              messages: s.messages.map(m =>
                m.id === 'streaming' ? { ...m, id: crypto.randomUUID() } : m
              ),
            }))
          } else if (event.type === 'error') {
            setState(s => ({
              ...s,
              isStreaming: false,
              error: (event as { message: string }).message,
              messages: s.messages.filter(m => m.id !== 'streaming'),
            }))
          }
        },
      )
    } catch (error) {
      setState(s => ({
        ...s,
        isStreaming: false,
        error: error instanceof Error ? error.message : 'Message failed',
        messages: s.messages.filter(m => m.id !== 'streaming'),
      }))
    }
  }, [state.sessionId, state.sessionToken, shareToken])

  const completeInterview = useCallback(async () => {
    if (!state.sessionId || !state.sessionToken) return
    setState(s => ({ ...s, isLoading: true }))
    try {
      await publicInterviewApi.completeInterview(
        shareToken,
        state.sessionId,
        state.sessionToken,
      )
      setState(s => ({ ...s, isLoading: false, phase: 'completed' }))
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to complete interview'
      setState(s => ({ ...s, isLoading: false, error: message }))
    }
  }, [state.sessionId, state.sessionToken, shareToken])

  return {
    ...state,
    isStarting: state.isLoading,
    messageCount: state.messages.length,
    fetchLanding,
    startInterview,
    sendMessage,
    completeInterview,
  }
}
